import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { getCustomerUrl } from "../../constants";
import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { FaArrowLeft, FaCopy, FaFilter, FaFileExcel } from "react-icons/fa";
import {
  FiLink,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiTrendingUp,
  FiPieChart,
  FiShoppingBag,
  FiActivity,
} from "react-icons/fi";
import { Spinner } from "reactstrap";
import { exportTrackingLinkToXLSX } from "./exportTrackingLink";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

export const TrackingLinkDetail = () => {
  const { callApi, navigate, configs } = Context();
  const { toast } = useToast();
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get("code") || "";

  const [linkInfo, setLinkInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRound, setSelectedRound] = useState(configs?.roundDate || "");
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'orders' | 'visits' | 'registers'

  useEffect(() => {
    if (code) {
      fetchDetail();
    }
  }, [code, selectedRound]);

  const fetchDetail = async () => {
    setLoading(true);

    const [linkRes, statsRes, eventsRes] = await Promise.all([
      callApi("trackingLinks-getTrackingLinkByCode", { code }, { notShowLoading: true }),
      callApi(
        "trackingLinks-getTrackingLinkStats",
        { code, roundDate: selectedRound },
        { notShowLoading: true }
      ),
      callApi(
        "trackingLinks-getTrackingLinkEvents",
        { code, limit: 500, roundDate: selectedRound },
        { notShowLoading: true }
      ),
    ]);

    if (linkRes && linkRes.status) {
      setLinkInfo(linkRes.data);
    }
    if (statsRes && statsRes.status) {
      setStats(statsRes.stats);
    }
    if (eventsRes && eventsRes.status) {
      setEvents(eventsRes.data || []);
    }

    setLoading(false);
  };

  const getFullUrl = (c) => {
    return getCustomerUrl(`/t/${c || code}`);
  };

  const copyLink = () => {
    const link = getFullUrl(code);
    navigator.clipboard.writeText(link);
    toast({
      className: "bg-[rgb(59,176,101)]",
      title: "คัดลอกลิงก์สำเร็จ",
      description: link,
    });
  };

  const handleExportXLSX = () => {
    try {
      const filename = exportTrackingLinkToXLSX({
        linkInfo,
        code,
        selectedRound,
        displayStats,
        ordersList,
        events,
      });
      toast({
        className: "bg-[rgb(59,176,101)]",
        title: "ดาวน์โหลดรายงาน Excel สำเร็จ",
        description: filename,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        className: "bg-[#c15b78]",
        title: "ดาวน์โหลดรายงานไม่สำเร็จ",
        description: error.message,
      });
    }
  };

  // คำนวณสถิติและหักยอดที่ยกเลิกออกจาก Events โดยตรง
  const eventStats = useMemo(() => {
    const visits = new Set();
    const registers = new Set();
    const orderLifecycle = {};
    let addToCarts = 0;
    let checkouts = 0;
    let payments = 0;

    events.forEach((evt) => {
      const orderKey = evt.orderId || "";
      const amount = Number(evt.orderAmount) || 0;
      const size = Number(evt.orderSize) || 0;

      if (orderKey) {
        if (!orderLifecycle[orderKey]) {
          orderLifecycle[orderKey] = {
            orderId: orderKey,
            amount: 0,
            size: 0,
            isApproved: false,
            isCancelled: false,
          };
        }
        if (amount && !orderLifecycle[orderKey].amount) orderLifecycle[orderKey].amount = amount;
        if (size && !orderLifecycle[orderKey].size) orderLifecycle[orderKey].size = size;
      }

      switch (evt.type) {
        case "visit":
          if (evt.sessionId) visits.add(evt.sessionId);
          break;
        case "register":
          registers.add(evt.userId || evt.id);
          break;
        case "add_to_cart":
          addToCarts++;
          break;
        case "checkout":
          checkouts++;
          break;
        case "payment":
          payments++;
          break;
        case "order_approved":
          if (orderKey) orderLifecycle[orderKey].isApproved = true;
          break;
        case "order_cancelled":
          if (orderKey) orderLifecycle[orderKey].isCancelled = true;
          break;
      }
    });

    let orderSuccesses = 0;
    let orderApproved = 0;
    let orderCancelled = 0;
    let grossRevenue = 0;
    let cancelledRevenue = 0;
    let netRevenue = 0;

    Object.values(orderLifecycle).forEach((ord) => {
      orderSuccesses++;
      const amt = ord.amount || 0;
      grossRevenue += amt;

      if (ord.isCancelled) {
        orderCancelled++;
        cancelledRevenue += amt;
      } else {
        netRevenue += amt;
        if (ord.isApproved) {
          orderApproved++;
        }
      }
    });

    return {
      visits: visits.size,
      registers: registers.size,
      addToCarts,
      checkouts,
      payments,
      orderSuccesses,
      orderApproved,
      orderCancelled,
      grossRevenue,
      cancelledRevenue,
      totalRevenue: netRevenue,
    };
  }, [events]);

  const displayStats = useMemo(() => {
    const visits = stats?.visits ?? eventStats.visits;
    const orderSuccesses = stats?.orderSuccesses || eventStats.orderSuccesses;
    const orderApproved = stats?.orderApproved || eventStats.orderApproved;
    const orderCancelled = stats?.orderCancelled || eventStats.orderCancelled;
    const grossRevenue = stats?.grossRevenue || eventStats.grossRevenue || 0;
    const cancelledRevenue = stats?.cancelledRevenue || eventStats.cancelledRevenue || 0;
    const totalRevenue =
      stats?.totalRevenue !== undefined
        ? stats.totalRevenue
        : eventStats.totalRevenue;

    const conversionRate =
      visits > 0 ? ((orderSuccesses / visits) * 100).toFixed(1) : "0.0";

    return {
      visits,
      registers: stats?.registers ?? eventStats.registers,
      addToCarts: stats?.addToCarts ?? eventStats.addToCarts,
      checkouts: stats?.checkouts ?? eventStats.checkouts,
      payments: stats?.payments ?? eventStats.payments,
      orderSuccesses,
      orderApproved,
      orderCancelled,
      grossRevenue,
      cancelledRevenue,
      totalRevenue,
      conversionRate,
    };
  }, [stats, eventStats]);

  // สรุปข้อมูล Timeline สำหรับกราฟ Trend
  const timelineData = useMemo(() => {
    if (!events || events.length === 0) return [];
    const grouped = {};

    const sorted = [...events].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

    sorted.forEach((e) => {
      if (!e.createdAt) return;
      const d = new Date(e.createdAt);
      const key = `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}`;

      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          visits: 0,
          registers: 0,
          orders: 0,
          revenue: 0,
        };
      }

      if (e.type === "visit") grouped[key].visits++;
      if (e.type === "register") grouped[key].registers++;
      if (e.type === "order_success") {
        grouped[key].orders++;
        grouped[key].revenue += Number(e.orderAmount) || 0;
      }
      if (e.type === "order_cancelled") {
        grouped[key].revenue = Math.max(0, grouped[key].revenue - (Number(e.orderAmount) || 0));
      }
    });

    return Object.values(grouped);
  }, [events]);

  // สรุปสัดส่วนสถานะ Order สำหรับ Pie Chart
  const pieData = useMemo(() => {
    const approved = displayStats.orderApproved || 0;
    const cancelled = displayStats.orderCancelled || 0;
    const pending = Math.max(
      0,
      (displayStats.orderSuccesses || 0) - approved - cancelled
    );

    const data = [];
    if (approved > 0) data.push({ name: "อนุมัติแล้ว", value: approved, color: "#10B981" });
    if (pending > 0) data.push({ name: "รอตรวจสลิป", value: pending, color: "#F59E0B" });
    if (cancelled > 0) data.push({ name: "ยกเลิกแล้ว", value: cancelled, color: "#EF4444" });
    return data;
  }, [displayStats]);

  // สรุปรายการคำสั่งซื้อเฉพาะตาม Order ID
  const ordersList = useMemo(() => {
    if (!events || events.length === 0) return [];
    const ordersMap = {};

    events.forEach((evt) => {
      if (!evt.orderId) return;
      if (!ordersMap[evt.orderId]) {
        ordersMap[evt.orderId] = {
          orderId: evt.orderId,
          orderShortId: evt.orderShortId || "",
          userId: evt.userId || "",
          customerName: evt.customerName || "",
          customerPhone: evt.customerPhone || "",
          isGuest: evt.isGuest || false,
          orderAmount: Number(evt.orderAmount) || 0,
          orderSize: Number(evt.orderSize) || 0,
          createdAt: evt.createdAt,
          status: "pending",
        };
      }

      const ord = ordersMap[evt.orderId];
      if (evt.customerName && !ord.customerName) ord.customerName = evt.customerName;
      if (evt.customerPhone && !ord.customerPhone) ord.customerPhone = evt.customerPhone;
      if (evt.isGuest !== undefined) ord.isGuest = evt.isGuest;
      if (evt.orderShortId && !ord.orderShortId) ord.orderShortId = evt.orderShortId;
      if (evt.orderAmount && !ord.orderAmount) ord.orderAmount = Number(evt.orderAmount) || 0;
      if (evt.orderSize && !ord.orderSize) ord.orderSize = Number(evt.orderSize) || 0;
      if (evt.createdAt && (!ord.createdAt || evt.createdAt < ord.createdAt)) {
        ord.createdAt = evt.createdAt;
      }

      if (evt.type === "order_approved") {
        ord.status = "approved";
      } else if (evt.type === "order_cancelled") {
        ord.status = "cancelled";
      }
    });

    return Object.values(ordersMap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [events]);

  // กรองรายการ Event ตามแท็บ
  const filteredEvents = useMemo(() => {
    if (activeTab === "visits") return events.filter((e) => e.type === "visit");
    if (activeTab === "registers") return events.filter((e) => e.type === "register");
    if (activeTab === "orders") {
      return events.filter((e) =>
        ["order_success", "order_approved", "order_cancelled"].includes(e.type)
      );
    }
    return events;
  }, [events, activeTab]);

  if (!code) {
    return (
      <div className="p-10 text-center">
        <p className="text-gray-500">ไม่พบข้อมูล Tracking Code</p>
        <button
          onClick={() => navigate("/tracking-links/manage")}
          className="mt-4 text-sm font-semibold text-navy hover:underline"
        >
          กลับไปหน้าจัดการ Tracking Links
        </button>
      </div>
    );
  }

  const getStepPercent = (val, base) => {
    if (!base || base === 0) return 0;
    return Math.round((val / base) * 100);
  };

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-10">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <button
          onClick={() => navigate("/tracking-links/manage")}
          className="p-2 bg-white rounded-md shadow-sm border text-navy hover:bg-gray-50"
        >
          <FaArrowLeft />
        </button>
        <Header
          header={`รายละเอียด Tracking Link: ${linkInfo?.name || code}`}
          subHeader={`Tracking Code: ${code}`}
          icon={<FiLink className="text-blue text-2xl" />}
        />
      </div>

      {/* Top Header Card */}
      <div className="bg-white p-4 rounded-md shadow-md mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-navy">{linkInfo?.name || code}</h2>
            <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono font-bold">
              {code}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-md max-w-fit">
            <FiLink className="text-gray-400 text-xs" />
            <span className="font-mono text-xs font-semibold text-navy select-all">
              {getFullUrl(code)}
            </span>
            <button
              onClick={copyLink}
              title="คัดลอกลิงก์"
              className="ml-2 text-navy hover:text-blue text-xs font-semibold flex items-center gap-1 bg-white px-2 py-0.5 rounded border shadow-sm transition hover:bg-gray-100"
            >
              <FaCopy className="text-xs" /> คัดลอก
            </button>
          </div>
          {linkInfo?.contact && (
            <p className="text-xs text-gray-500 mt-1.5">อ้างอิง: {linkInfo.contact}</p>
          )}
          {linkInfo?.note && (
            <p className="text-xs text-gray-400 mt-0.5">{linkInfo.note}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-sm font-semibold">
            <span>งวด</span>
            <select
              value={selectedRound}
              onChange={(e) => setSelectedRound(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm font-semibold"
            >
              <option value="">ทั้งหมด</option>
              {configs?.roundDates?.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={copyLink}
            className="py-2.5 px-4 bg-navy text-white text-sm font-bold rounded-md hover:opacity-90 flex items-center gap-2"
          >
            <FaCopy /> คัดลอกลิงก์
          </button>

          <button
            onClick={handleExportXLSX}
            disabled={loading}
            className="py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-md flex items-center gap-2 shadow-sm transition disabled:opacity-50"
          >
            <FaFileExcel className="text-base" /> ดาวน์โหลด Excel (.xlsx)
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center">
          <Spinner />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-6 gap-4 mb-6">
            <div className="bg-white p-4 rounded-md shadow-md border-l-4 border-blue-500">
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold text-gray-500">จำนวนเข้าชม (Visits)</p>
                <FiLink className="text-blue-500 text-lg" />
              </div>
              <p className="text-2xl font-bold text-navy mt-2">
                {(displayStats.visits || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-4 rounded-md shadow-md border-l-4 border-indigo-500">
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold text-gray-500">สมัครสมาชิก</p>
                <FiUsers className="text-indigo-500 text-lg" />
              </div>
              <p className="text-2xl font-bold text-navy mt-2">
                {(displayStats.registers || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-4 rounded-md shadow-md border-l-4 border-green-500">
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold text-gray-500">สั่งซื้อทั้งหมด</p>
                <FiCheckCircle className="text-green-500 text-lg" />
              </div>
              <p className="text-2xl font-bold text-navy mt-2">
                {(displayStats.orderSuccesses || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-4 rounded-md shadow-md border-l-4 border-emerald-500">
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold text-gray-500">อนุมัติสุทธิ</p>
                <FiCheckCircle className="text-emerald-500 text-lg" />
              </div>
              <p className="text-2xl font-bold text-emerald-700 mt-2">
                {(displayStats.orderApproved || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-4 rounded-md shadow-md border-l-4 border-red-400">
              <div className="flex justify-between items-center">
                <p className="text-xs font-semibold text-gray-500">ยกเลิก</p>
                <FiXCircle className="text-red-400 text-lg" />
              </div>
              <p className="text-2xl font-bold text-red-600 mt-2">
                {(displayStats.orderCancelled || 0).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-4 rounded-md shadow-md border-l-4 border-amber-500">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-semibold text-gray-500">ยอดขายสุทธิ</p>
                  <p className="text-[10px] text-gray-400">(หักยกเลิกแล้ว)</p>
                </div>
                <span className="text-amber-500 font-bold">฿</span>
              </div>
              <p className="text-2xl font-bold text-navy mt-1">
                ฿{(displayStats.totalRevenue || 0).toLocaleString()}
              </p>
              {displayStats.cancelledRevenue > 0 && (
                <p className="text-[10px] text-red-500 font-semibold mt-0.5">
                  ยกเลิก: -฿{displayStats.cancelledRevenue.toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Charts Section: Timeline Trend & Pie Breakdown */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            {/* Timeline Area Chart */}
            <div className="col-span-2 bg-white p-5 rounded-md shadow-md">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FiTrendingUp className="text-navy text-lg" />
                  <h3 className="text-base font-bold text-navy">แนวโน้มกิจกรรมและยอดขาย</h3>
                </div>
                <span className="text-xs text-gray-400">เรียงตามวัน</span>
              </div>

              {timelineData.length > 0 ? (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={timelineData}>
                      <defs>
                        <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          border: "none",
                          fontSize: "12px",
                        }}
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="visits"
                        name="Visits"
                        stroke="#3B82F6"
                        fillOpacity={1}
                        fill="url(#colorVisits)"
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="revenue"
                        name="ยอดขาย (฿)"
                        stroke="#10B981"
                        fillOpacity={1}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
                  ไม่มีข้อมูลกราฟกิจกรรม
                </div>
              )}
            </div>

            {/* Order Status Pie Chart */}
            <div className="bg-white p-5 rounded-md shadow-md flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FiPieChart className="text-navy text-lg" />
                  <h3 className="text-base font-bold text-navy">สัดส่วนสถานะคำสั่งซื้อ</h3>
                </div>

                {pieData.length > 0 ? (
                  <div className="h-48 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          paddingAngle={4}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
                    ยังไม่มีข้อมูลคำสั่งซื้อ
                  </div>
                )}
              </div>

              <div className="border-t pt-3 flex justify-around text-center text-xs">
                <div>
                  <p className="text-gray-400 font-semibold">อนุมัติแล้ว</p>
                  <p className="font-bold text-emerald-600 text-sm">
                    {displayStats.orderApproved}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 font-semibold">ยกเลิก</p>
                  <p className="font-bold text-red-600 text-sm">
                    {displayStats.orderCancelled}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 font-semibold">Conversion</p>
                  <p className="font-bold text-navy text-sm">
                    {displayStats.conversionRate}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Conversion Funnel Visualizer */}
          <div className="bg-white p-6 rounded-md shadow-md mb-6">
            <h3 className="text-xl font-bold text-navy mb-4">Conversion Funnel Breakdown</h3>

            <div className="grid grid-cols-7 gap-2 text-center">
              {/* Step 1: Visit */}
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <p className="text-xs font-semibold text-gray-600 mb-1">1. Visit</p>
                <p className="text-lg font-bold text-navy">{displayStats.visits || 0}</p>
                <p className="text-xs font-semibold text-gray-400 mt-1">100%</p>
              </div>

              {/* Step 2: Register */}
              <div className="bg-indigo-50 p-3 rounded-md border border-indigo-200">
                <p className="text-xs font-semibold text-gray-600 mb-1">2. Register</p>
                <p className="text-lg font-bold text-navy">{displayStats.registers || 0}</p>
                <p className="text-xs font-semibold text-gray-400 mt-1">
                  {getStepPercent(displayStats.registers, displayStats.visits)}%
                </p>
              </div>

              {/* Step 3: Add to Cart */}
              <div className="bg-purple-50 p-3 rounded-md border border-purple-200">
                <p className="text-xs font-semibold text-gray-600 mb-1">3. Add to Cart</p>
                <p className="text-lg font-bold text-navy">{displayStats.addToCarts || 0}</p>
                <p className="text-xs font-semibold text-gray-400 mt-1">
                  {getStepPercent(displayStats.addToCarts, displayStats.visits)}%
                </p>
              </div>

              {/* Step 4: Checkout */}
              <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                <p className="text-xs font-semibold text-gray-600 mb-1">4. Checkout</p>
                <p className="text-lg font-bold text-navy">{displayStats.checkouts || 0}</p>
                <p className="text-xs font-semibold text-gray-400 mt-1">
                  {getStepPercent(displayStats.checkouts, displayStats.visits)}%
                </p>
              </div>

              {/* Step 5: Payment */}
              <div className="bg-orange-50 p-3 rounded-md border border-orange-200">
                <p className="text-xs font-semibold text-gray-600 mb-1">5. Payment</p>
                <p className="text-lg font-bold text-navy">{displayStats.payments || 0}</p>
                <p className="text-xs font-semibold text-gray-400 mt-1">
                  {getStepPercent(displayStats.payments, displayStats.visits)}%
                </p>
              </div>

              {/* Step 6: Order Success */}
              <div className="bg-green-50 p-3 rounded-md border border-green-200">
                <p className="text-xs font-semibold text-gray-600 mb-1">6. สร้างคำสั่งซื้อ</p>
                <p className="text-lg font-bold text-green-700">{displayStats.orderSuccesses || 0}</p>
                <p className="text-xs font-bold text-green-600 mt-1">
                  {displayStats.conversionRate || 0}%
                </p>
              </div>

              {/* Step 7: Approved */}
              {/* <div className="bg-emerald-50 p-3 rounded-md border border-emerald-200">
                <p className="text-xs font-semibold text-gray-600 mb-1">7. อนุมัติแล้ว</p>
                <p className="text-lg font-bold text-emerald-700">{displayStats.orderApproved || 0}</p>
                <p className="text-xs font-semibold text-emerald-600 mt-1">
                  {getStepPercent(displayStats.orderApproved, displayStats.orderSuccesses)}%
                </p>
              </div> */}
            </div>
          </div>

          {/* Orders Breakdown Table */}
          {ordersList.length > 0 && (
            <div className="bg-white rounded-md shadow-md overflow-hidden mb-6">
              <div className="p-4 border-b flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <FiShoppingBag className="text-navy text-lg" />
                  <h3 className="text-xl font-bold text-navy">
                    รายการคำสั่งซื้อ ({ordersList.length} รายการ)
                  </h3>
                </div>
                <div className="text-xs text-gray-500 font-semibold">
                  ยอดรวมสุทธิ:{" "}
                  <strong className="text-emerald-600 text-sm">
                    ฿{displayStats.totalRevenue.toLocaleString()}
                  </strong>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>วันที่/เวลา</TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>ข้อมูลผู้ซื้อ</TableHead>
                    <TableHead>User ID / บัญชี</TableHead>
                    <TableHead className="text-right">ยอดเงิน (บาท)</TableHead>
                    <TableHead className="text-center">จำนวนสลาก</TableHead>
                    <TableHead className="text-center">สถานะปัจจุบัน</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ordersList.map((ord) => (
                    <TableRow key={ord.orderId}>
                      <TableCell className="text-xs text-gray-500">
                        {ord.createdAt ? new Date(ord.createdAt).toLocaleString("th-TH") : "-"}
                      </TableCell>
                      <TableCell className="text-xs font-mono font-bold text-navy">
                        {ord.orderShortId ? `#${ord.orderShortId}` : ord.orderId}
                        {ord.orderShortId && (
                          <p className="text-[10px] font-normal text-gray-400 font-mono truncate max-w-[120px]" title={ord.orderId}>
                            {ord.orderId}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-xs font-bold text-navy">
                            {ord.customerName || (ord.isGuest ? "ลูกค้าทั่วไป (Guest)" : "สมาชิก")}
                          </span>
                          <span className="text-[11px] text-gray-500 font-mono">
                            {ord.customerPhone || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${ord.isGuest ? "bg-gray-100 text-gray-600 border" : "bg-blue-100 text-blue-800"
                              }`}
                          >
                            {ord.isGuest ? "Guest" : "Member"}
                          </span>
                          <span className="text-xs font-mono text-gray-600 truncate max-w-[100px]" title={ord.userId}>
                            {ord.userId || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {ord.orderAmount ? `฿${ord.orderAmount.toLocaleString()}` : "-"}
                      </TableCell>
                      <TableCell className="text-center font-semibold">
                        {ord.orderSize ? `${ord.orderSize} ใบ` : "-"}
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={`px-2.5 py-1 rounded text-xs font-bold ${ord.status === "approved"
                            ? "bg-emerald-100 text-emerald-800"
                            : ord.status === "cancelled"
                              ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                            }`}
                        >
                          {ord.status === "approved"
                            ? "✓ อนุมัติแล้ว"
                            : ord.status === "cancelled"
                              ? "✕ ยกเลิกแล้ว"
                              : "รอตรวจสลิป"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Activity Log Table */}
          <div className="bg-white rounded-md shadow-md overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-2">
                <FiActivity className="text-navy text-lg" />
                <h3 className="text-xl font-bold text-navy">Activity Log (ประวัติกิจกรรม)</h3>
              </div>

              {/* Event Filter Tabs */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-md text-xs font-semibold">
                <button
                  onClick={() => setActiveTab("all")}
                  className={`px-3 py-1 rounded transition ${activeTab === "all" ? "bg-white shadow-sm text-navy" : "text-gray-500 hover:text-navy"
                    }`}
                >
                  ทั้งหมด ({events.length})
                </button>
                <button
                  onClick={() => setActiveTab("visits")}
                  className={`px-3 py-1 rounded transition ${activeTab === "visits" ? "bg-white shadow-sm text-navy" : "text-gray-500 hover:text-navy"
                    }`}
                >
                  Visits
                </button>
                <button
                  onClick={() => setActiveTab("registers")}
                  className={`px-3 py-1 rounded transition ${activeTab === "registers" ? "bg-white shadow-sm text-navy" : "text-gray-500 hover:text-navy"
                    }`}
                >
                  สมัคร
                </button>
                <button
                  onClick={() => setActiveTab("orders")}
                  className={`px-3 py-1 rounded transition ${activeTab === "orders" ? "bg-white shadow-sm text-navy" : "text-gray-500 hover:text-navy"
                    }`}
                >
                  ออเดอร์
                </button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>เวลา</TableHead>
                  <TableHead>ประเภท Event</TableHead>
                  <TableHead>ข้อมูลผู้ใช้ / ผู้ซื้อ</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead className="text-right">ยอดเงิน (บาท)</TableHead>
                  <TableHead className="text-center">จำนวนสลาก</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents && filteredEvents.length > 0 ? (
                  filteredEvents.map((evt) => (
                    <TableRow key={evt.id}>
                      <TableCell className="text-xs text-gray-500">
                        {new Date(evt.createdAt).toLocaleString("th-TH")}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold ${evt.type === "visit"
                            ? "bg-blue-100 text-blue-800"
                            : evt.type === "register"
                              ? "bg-indigo-100 text-indigo-800"
                              : evt.type === "add_to_cart"
                                ? "bg-purple-100 text-purple-800"
                                : evt.type === "checkout"
                                  ? "bg-amber-100 text-amber-800"
                                  : evt.type === "payment"
                                    ? "bg-orange-100 text-orange-800"
                                    : evt.type === "order_approved"
                                      ? "bg-emerald-100 text-emerald-800"
                                      : evt.type === "order_cancelled"
                                        ? "bg-red-100 text-red-800"
                                        : "bg-green-100 text-green-800"
                            }`}
                        >
                          {evt.type}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          {evt.customerName && (
                            <span className="text-xs font-bold text-navy">
                              {evt.customerName}
                            </span>
                          )}
                          <span className="text-xs font-mono text-gray-600">
                            {evt.customerPhone || evt.userId || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {evt.orderShortId ? `#${evt.orderShortId}` : evt.orderId || "-"}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {evt.orderAmount ? `฿${evt.orderAmount.toLocaleString()}` : "-"}
                      </TableCell>
                      <TableCell className="text-center font-semibold">
                        {evt.orderSize ? `${evt.orderSize} ใบ` : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6 text-gray-400">
                      ยังไม่มีประวัติกิจกรรมในหมวดนี้
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};
